import { useAtomValue, useSetAtom } from 'jotai';

import { Unit } from '@core';
import { ParameterNode, SwingAnalysis } from '@data';
import { useUserSettingsWithDefaults } from '@hooks';
import { useTranslate } from '@i18n';
import { useActivityReport, useSwing, useSwingAnalysis } from '@queries';
import {
    activityUnitAtom,
    convertedLayoutCustomCorridorsMapAtom,
    corridorsAtom,
    selectedSwingGuideParameterAtom,
    setGoalCorridorParameterAtom,
    swingGuideParametersAtom,
} from '@store';
import { MenuItem, SwingFoundationParameterSelectionCard } from '@ui';

type NewParameterProps = {
    onClick?: (node: ParameterNode) => void;
    isSelected?: boolean;
    node: ParameterNode;
    activityId?: number;
    activeSwingId?: string;
    activeUserId?: string;
    showCorridors?: boolean;
};
export function getParameterValue(analysis: SwingAnalysis | undefined, parameterID: string): number | undefined {
    return (
        analysis?.fullAnalysis?.data?.analysis?.parameter_values?.[parameterID]?.value ??
        analysis?.quickAnalysis?.data?.analysis?.parameter_values?.[parameterID]?.value
    );
}

export const NewParameter = ({
    onClick,
    isSelected,
    node,
    activityId,
    activeSwingId,
    activeUserId,
    showCorridors,
}: NewParameterProps) => {
    const { i18n } = node;

    const parameterName = node.parameter.name;
    const parameterId = node.parameter.smpId;
    const parameterUnit = node.parameter.unit;

    const activityUnit = useAtomValue(activityUnitAtom);
    const layoutCorridors = useAtomValue(convertedLayoutCustomCorridorsMapAtom);
    const corridors = useAtomValue(corridorsAtom);
    const swingGuideParameters = useAtomValue(swingGuideParametersAtom);
    const setGoalCorridorParameter = useSetAtom(setGoalCorridorParameterAtom);
    const setSelectedGuideParameterId = useSetAtom(selectedSwingGuideParameterAtom);
    const { data: userSettings } = useUserSettingsWithDefaults(activeUserId);
    const { data: activityReport } = useActivityReport(activityId);

    const { data: activeSwing } = useSwing(activeSwingId || '');

    const { data: activeSwingAnalysis } = useSwingAnalysis(activeSwing?.uuid);

    const { data: comparisonSwingAnalysis } = useSwingAnalysis(userSettings.comparisonSwingId ?? undefined);
    const parameterValue = getParameterValue(activeSwingAnalysis, parameterName);
    const comparisonParameterValue = getParameterValue(comparisonSwingAnalysis, parameterName);
    const customCorridor = layoutCorridors?.[parameterId];
    const reportValue = activityReport?.[parameterName];

    const t = useTranslate();

    if (!parameterUnit || !node.parameter) {
        return null;
    }

    return (
        <SwingFoundationParameterSelectionCard
            userPreferredUnit={activityUnit}
            value={parameterValue}
            title={t(i18n ?? '-')}
            unit={parameterUnit as Unit}
            onClick={onClick ? () => onClick(node) : undefined}
            isSelected={isSelected}
            showCorridors={showCorridors}
            comparisonValue={userSettings.showComparison ? comparisonParameterValue : null}
            goalCorridor={userSettings.showGoal ? customCorridor : null}
            corridorParameterMargins={userSettings.showCorridors ? corridors?.[parameterName] : null}
            mean={reportValue?.mean}
            stdDev={reportValue?.stdDev}
            menuItems={
                <>
                    <MenuItem
                        onClick={(event) => {
                            event.stopPropagation();
                            setGoalCorridorParameter(node);
                        }}
                    >
                        Add/adjust goal corridors
                    </MenuItem>
                    {activeSwingId && swingGuideParameters.has(node.parameter.smpId) && (
                        <MenuItem
                            onClick={(event) => {
                                event.stopPropagation();
                                setSelectedGuideParameterId(node.parameter.smpId);
                            }}
                        >
                            Guide
                        </MenuItem>
                    )}
                </>
            }
        />
    );
};
